import { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { Badge } from '@/components/ui/Badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { ItemPicker } from '@/components/inventory/ItemPicker';
import { useAuth } from '@/app/AuthProvider';
import {
  useBomForItem,
  useInventoryLevels,
  useItems,
  useLocations,
  useWorkstations,
  type Item,
} from '@/features/inventory/hooks';
import { effectiveUnitCost, useItemCostSummaries } from '@/features/procurement/hooks';
import {
  useSubmitProductionForm,
  type ProductionComponentInput,
} from '@/features/manufacturing/hooks';
import { useProfiles } from '@/features/users/hooks';
import { formatCurrency, todayIsoDate } from '@/lib/formatters';

const NONE = '__none__';

interface ComponentDraft {
  key: string;
  itemId: string;
  bomLineId: string | null;
  /** BOM quantity per finished unit, scrap allowance included. Null for hand-added rows. */
  perUnit: number | null;
  quantity: string;
  /** Set once the operator edits the quantity, so qty changes stop overwriting it. */
  touched: boolean;
  locationId: string;
  lotNumber: string;
}

function wholeUnits(value: string): number {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : 0;
}

function expectedQuantity(perUnit: number, units: number): number {
  // Round away float noise (0.1 * 3 = 0.30000000000000004) before ceiling.
  return Math.ceil(Number((perUnit * units).toFixed(4)));
}

interface ProductionFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Production form: record anything built in-house coming off the line —
 * finished goods, sub-assemblies, or components. The good
 * units are added to on-hand at the put-away location, and the components
 * (pre-filled from the item's active BOM, scaled to good + scrap units)
 * are consumed from wherever they were pulled.
 */
export function ProductionFormDialog({ open, onOpenChange }: ProductionFormDialogProps) {
  const { profile } = useAuth();
  const submit = useSubmitProductionForm();
  const { data: items } = useItems();
  const { data: locations } = useLocations();
  const { data: workstations } = useWorkstations();
  const { data: profiles } = useProfiles();
  const { data: levels } = useInventoryLevels();
  const { data: costSummaries } = useItemCostSummaries();

  const [itemId, setItemId] = useState<string | null>(null);
  const [producedOn, setProducedOn] = useState(todayIsoDate());
  const [quantity, setQuantity] = useState('');
  const [scrap, setScrap] = useState('');
  const [toLocationId, setToLocationId] = useState('');
  const [lotNumber, setLotNumber] = useState('');
  const [workstationId, setWorkstationId] = useState(NONE);
  const [producedBy, setProducedBy] = useState('');
  const [notes, setNotes] = useState('');
  const [components, setComponents] = useState<ComponentDraft[]>([]);
  const [addingComponent, setAddingComponent] = useState(false);

  const { data: bom, isLoading: bomLoading } = useBomForItem(itemId ?? undefined);

  const itemById = useMemo(() => new Map((items ?? []).map((it) => [it.id, it])), [items]);
  const item = itemId ? itemById.get(itemId) ?? null : null;
  const locationRows = useMemo(() => locations ?? [], [locations]);
  const activeProfiles = useMemo(() => (profiles ?? []).filter((p) => p.is_active), [profiles]);

  const bomVersion = useMemo(() => {
    const versions = bom?.bom_versions ?? [];
    return versions.find((v) => v.is_active) ?? [...versions].sort((a, b) => b.version - a.version)[0] ?? null;
  }, [bom]);

  /** On-hand keyed by `${itemId}:${locationId}`, summed across lots. */
  const onHand = useMemo(() => {
    const map = new Map<string, number>();
    for (const l of levels ?? []) {
      const key = `${l.item_id}:${l.location_id}`;
      map.set(key, (map.get(key) ?? 0) + l.quantity);
    }
    return map;
  }, [levels]);

  const defaultPutAway =
    locationRows.find((l) => l.kind === 'warehouse')?.id ?? locationRows[0]?.id ?? '';
  const productionFloor = locationRows.find((l) => l.kind === 'production_floor')?.id ?? defaultPutAway;

  /** Where a component is most likely pulled from: the location holding the most of it. */
  const defaultSourceFor = (componentId: string): string => {
    let best = '';
    let bestQty = 0;
    for (const loc of locationRows) {
      const qty = onHand.get(`${componentId}:${loc.id}`) ?? 0;
      if (qty > bestQty) {
        best = loc.id;
        bestQty = qty;
      }
    }
    return best || productionFloor;
  };

  useEffect(() => {
    if (!open) return;
    setItemId(null);
    setProducedOn(todayIsoDate());
    setQuantity('');
    setScrap('');
    setLotNumber('');
    setWorkstationId(NONE);
    setNotes('');
    setComponents([]);
    setAddingComponent(false);
  }, [open]);

  useEffect(() => {
    if (open && !producedBy && profile?.id) setProducedBy(profile.id);
  }, [open, producedBy, profile?.id]);

  useEffect(() => {
    if (open && !toLocationId && defaultPutAway) setToLocationId(defaultPutAway);
  }, [open, toLocationId, defaultPutAway]);

  const totalUnits = wholeUnits(quantity) + wholeUnits(scrap);

  // Rebuild component rows only when the BOM version for the selected item
  // changes, not on every refetch (which would wipe edits).
  const builtFor = useRef<string | null>(null);
  useEffect(() => {
    if (!open || !itemId) {
      builtFor.current = null;
      return;
    }
    if (bomLoading) return;
    const signature = `${itemId}:${bomVersion?.id ?? 'none'}`;
    if (builtFor.current === signature) return;
    builtFor.current = signature;
    setComponents(
      (bomVersion?.bom_lines ?? [])
        .filter((l) => l.items?.id)
        .map((l) => {
          const perUnit = Number(l.quantity) * (1 + Number(l.scrap_pct ?? 0) / 100);
          return {
            key: l.id,
            itemId: l.items!.id,
            bomLineId: l.id,
            perUnit,
            quantity: totalUnits > 0 ? String(expectedQuantity(perUnit, totalUnits)) : '',
            touched: false,
            locationId: defaultSourceFor(l.items!.id),
            lotNumber: '',
          };
        }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rebuild keyed on the BOM signature only
  }, [open, itemId, bomLoading, bomVersion]);

  // Keep untouched BOM rows in step with good + scrap units.
  useEffect(() => {
    setComponents((prev) =>
      prev.map((c) =>
        c.touched || c.perUnit == null
          ? c
          : { ...c, quantity: totalUnits > 0 ? String(expectedQuantity(c.perUnit, totalUnits)) : '' },
      ),
    );
  }, [totalUnits]);

  const updateComponent = (key: string, patch: Partial<ComponentDraft>) =>
    setComponents((prev) => prev.map((c) => (c.key === key ? { ...c, ...patch } : c)));

  const addComponent = (picked: Item | null) => {
    if (!picked) return;
    setAddingComponent(false);
    if (components.some((c) => c.itemId === picked.id)) {
      toast.info(`${picked.sku} is already on this form.`);
      return;
    }
    setComponents((prev) => [
      ...prev,
      {
        key: `manual-${picked.id}`,
        itemId: picked.id,
        bomLineId: null,
        perUnit: null,
        quantity: '',
        touched: true,
        locationId: defaultSourceFor(picked.id),
        lotNumber: '',
      },
    ]);
  };

  const unitCostOf = (componentId: string) =>
    effectiveUnitCost(costSummaries?.get(componentId), itemById.get(componentId)?.std_cost_cents);

  const consuming = components.filter((c) => wholeUnits(c.quantity) > 0);
  const estComponentCost = consuming.reduce(
    (acc, c) => acc + wholeUnits(c.quantity) * (unitCostOf(c.itemId) ?? 0),
    0,
  );
  const missingCost = consuming.some((c) => unitCostOf(c.itemId) == null);
  const goodUnits = wholeUnits(quantity);
  const estUnitCost = goodUnits > 0 ? Math.round(estComponentCost / goodUnits) : null;
  const shortCount = consuming.filter(
    (c) => wholeUnits(c.quantity) > (onHand.get(`${c.itemId}:${c.locationId}`) ?? 0),
  ).length;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!item) {
      toast.error('Pick the item that was produced.');
      return;
    }
    if (goodUnits <= 0) {
      toast.error('Enter the number of good units produced.');
      return;
    }
    if (scrap.trim() !== '' && !Number.isInteger(Number(scrap))) {
      toast.error('Scrap must be a whole number.');
      return;
    }
    if (!toLocationId) {
      toast.error('Pick where the produced units were put away.');
      return;
    }
    if (item.is_lot_tracked && !lotNumber.trim()) {
      toast.error(`${item.sku} is lot-tracked — enter the lot / batch number.`);
      return;
    }

    const payload: ProductionComponentInput[] = [];
    for (const c of components) {
      const component = itemById.get(c.itemId);
      const label = component?.sku ?? 'Component';
      if (c.quantity.trim() === '') continue;
      const qty = Number(c.quantity);
      if (!Number.isInteger(qty) || qty < 0) {
        toast.error(`${label}: quantity must be a whole number.`);
        return;
      }
      if (qty === 0) continue;
      if (!c.locationId) {
        toast.error(`${label}: pick the location it was pulled from.`);
        return;
      }
      payload.push({
        item_id: c.itemId,
        quantity: qty,
        from_location_id: c.locationId,
        bom_line_id: c.bomLineId,
        expected_quantity: c.perUnit != null ? Number((c.perUnit * totalUnits).toFixed(4)) : null,
        lot_number: component?.is_lot_tracked ? c.lotNumber.trim() || null : null,
      });
    }

    try {
      await submit.mutateAsync({
        itemId: item.id,
        quantity: goodUnits,
        scrapQuantity: wholeUnits(scrap),
        toLocationId,
        lotNumber: lotNumber.trim() || null,
        workstationId: workstationId === NONE ? null : workstationId,
        // Today keeps the actual submission time so same-day forms stay in
        // order; back-dated forms land at noon local so the date is stable
        // across time zones.
        producedAt:
          producedOn === todayIsoDate()
            ? new Date().toISOString()
            : new Date(`${producedOn}T12:00:00`).toISOString(),
        producedBy: producedBy || null,
        bomVersionId: bomVersion?.id ?? null,
        notes: notes.trim() || null,
        components: payload,
      });
      toast.success(`Recorded ${goodUnits.toLocaleString()} × ${item.sku}`);
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not submit the production form.');
    }
  };

  const submitting = submit.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Record production</DialogTitle>
          </DialogHeader>

          <div className="mt-5 space-y-6">
            {item ? (
              <div className="flex flex-wrap items-center gap-3 rounded-md border border-border bg-secondary/30 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-xs text-muted-foreground">{item.sku}</div>
                  <div className="truncate font-medium">{item.name}</div>
                </div>
                {item.is_lot_tracked && <Badge variant="info-soft">Lot-tracked</Badge>}
                {bomLoading ? (
                  <Badge variant="muted">Loading BOM…</Badge>
                ) : bomVersion ? (
                  <Badge variant="secondary">BOM v{bomVersion.version}</Badge>
                ) : (
                  <Badge variant="warning-soft">No BOM</Badge>
                )}
                <Button type="button" variant="ghost" size="sm" onClick={() => setItemId(null)}>
                  <X className="h-4 w-4" />
                  Change
                </Button>
              </div>
            ) : (
              <ItemPicker
                label="Item produced *"
                value={itemId}
                onChange={(picked) => setItemId(picked?.id ?? null)}
                placeholder="Search finished goods, sub-assemblies, or components by SKU or name…"
                filter={(it) => it.is_active}
                autoFocus
              />
            )}

            <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-1.5">
                <Label htmlFor="prd-qty">
                  Good units <span className="text-fresh-salmon-dark">*</span>
                </Label>
                <Input
                  id="prd-qty"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value.replace(/[^\d]/g, ''))}
                  inputMode="numeric"
                  placeholder="0"
                  className="tabular-nums"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="prd-scrap">Scrapped units</Label>
                <Input
                  id="prd-scrap"
                  value={scrap}
                  onChange={(e) => setScrap(e.target.value.replace(/[^\d]/g, ''))}
                  inputMode="numeric"
                  placeholder="0"
                  className="tabular-nums"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="prd-date">Produced on</Label>
                <Input
                  id="prd-date"
                  type="date"
                  value={producedOn}
                  max={todayIsoDate()}
                  onChange={(e) => setProducedOn(e.target.value || todayIsoDate())}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="prd-lot">
                  Lot / batch #{item?.is_lot_tracked && <span className="text-fresh-salmon-dark"> *</span>}
                </Label>
                <Input
                  id="prd-lot"
                  value={lotNumber}
                  onChange={(e) => setLotNumber(e.target.value)}
                  placeholder={item?.is_lot_tracked ? 'Required' : 'Optional'}
                  maxLength={120}
                  className="font-mono text-xs"
                />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="prd-location">
                  Put away to <span className="text-fresh-salmon-dark">*</span>
                </Label>
                <Select value={toLocationId} onValueChange={setToLocationId}>
                  <SelectTrigger id="prd-location">
                    <SelectValue placeholder="Pick a location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locationRows.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>
                        {loc.name} ({loc.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="prd-workstation">Workstation</Label>
                <Select value={workstationId} onValueChange={setWorkstationId}>
                  <SelectTrigger id="prd-workstation">
                    <SelectValue placeholder="Optional" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>None</SelectItem>
                    {(workstations ?? []).map((w) => (
                      <SelectItem key={w.id} value={w.id}>
                        {w.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="prd-builder">Built by</Label>
                <Select value={producedBy} onValueChange={setProducedBy}>
                  <SelectTrigger id="prd-builder">
                    <SelectValue placeholder="Pick a person" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeProfiles.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.full_name || p.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </section>

            {item && (
              <section className="space-y-2">
                <div className="flex flex-wrap items-end justify-between gap-2">
                  <div>
                    <h3 className="text-sm font-medium">Components consumed</h3>
                    {bomVersion && (
                      <p className="text-xs text-muted-foreground">
                        BOM v{bomVersion.version} · {totalUnits.toLocaleString()} unit
                        {totalUnits === 1 ? '' : 's'}
                      </p>
                    )}
                  </div>
                  {!addingComponent && (
                    <Button type="button" variant="outline" size="sm" onClick={() => setAddingComponent(true)}>
                      <Plus className="h-4 w-4" />
                      Add component
                    </Button>
                  )}
                </div>

                {addingComponent && (
                  <div className="space-y-2 rounded-md border border-dashed border-border p-3">
                    <ItemPicker
                      label=""
                      value={null}
                      onChange={addComponent}
                      placeholder="Search components by SKU or name…"
                      filter={(it) => it.is_active && it.id !== item.id}
                      autoFocus
                    />
                    <div className="flex justify-end">
                      <Button type="button" variant="ghost" size="sm" onClick={() => setAddingComponent(false)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {components.length > 0 ? (
                  <div className="overflow-x-auto rounded-md border border-border">
                    <table className="w-full text-sm">
                      <thead className="bg-secondary/50 text-xs text-muted-foreground">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium">Component</th>
                          <th className="w-24 px-2 py-2 text-right font-medium">BOM / unit</th>
                          <th className="w-24 px-2 py-2 text-right font-medium">Consumed</th>
                          <th className="w-52 px-2 py-2 text-left font-medium">Pulled from</th>
                          <th className="w-32 px-2 py-2 text-left font-medium">Lot</th>
                          <th className="w-24 px-2 py-2 text-right font-medium">Est. cost</th>
                          <th className="w-10 px-2 py-2" />
                        </tr>
                      </thead>
                      <tbody>
                        {components.map((c) => {
                          const component = itemById.get(c.itemId);
                          const qty = wholeUnits(c.quantity);
                          const available = onHand.get(`${c.itemId}:${c.locationId}`) ?? 0;
                          const short = qty > available;
                          const unitCost = unitCostOf(c.itemId);
                          return (
                            <tr key={c.key} className="border-t border-border align-top">
                              <td className="px-3 py-2">
                                <div className="font-mono text-xs text-muted-foreground">{component?.sku ?? '—'}</div>
                                <div className="truncate">{component?.name ?? 'Unknown item'}</div>
                              </td>
                              <td className="px-2 py-2 text-right tabular-nums text-muted-foreground">
                                {c.perUnit != null ? Number(c.perUnit.toFixed(4)).toLocaleString() : 'Added'}
                                <div className="text-[11px]">{component?.base_uom}</div>
                              </td>
                              <td className="px-2 py-2">
                                <Input
                                  value={c.quantity}
                                  onChange={(e) =>
                                    updateComponent(c.key, {
                                      quantity: e.target.value.replace(/[^\d]/g, ''),
                                      touched: true,
                                    })
                                  }
                                  inputMode="numeric"
                                  placeholder="0"
                                  aria-label={`Quantity consumed of ${component?.sku ?? 'component'}`}
                                  className="h-8 text-right tabular-nums"
                                />
                              </td>
                              <td className="px-2 py-2">
                                <Select
                                  value={c.locationId}
                                  onValueChange={(v) => updateComponent(c.key, { locationId: v })}
                                >
                                  <SelectTrigger className="h-8" aria-label={`Location for ${component?.sku ?? 'component'}`}>
                                    <SelectValue placeholder="Pick a location" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {locationRows.map((loc) => (
                                      <SelectItem key={loc.id} value={loc.id}>
                                        {loc.name} ({loc.code})
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <div
                                  className={
                                    short
                                      ? 'mt-0.5 text-[11px] text-fresh-salmon-dark'
                                      : 'mt-0.5 text-[11px] text-muted-foreground'
                                  }
                                >
                                  {available.toLocaleString()} on hand{short && qty > 0 ? ' — short' : ''}
                                </div>
                              </td>
                              <td className="px-2 py-2">
                                {component?.is_lot_tracked ? (
                                  <Input
                                    value={c.lotNumber}
                                    onChange={(e) => updateComponent(c.key, { lotNumber: e.target.value })}
                                    placeholder="Lot #"
                                    maxLength={120}
                                    aria-label={`Lot number for ${component.sku}`}
                                    className="h-8 font-mono text-xs"
                                  />
                                ) : (
                                  <span className="text-xs text-muted-foreground">—</span>
                                )}
                              </td>
                              <td className="px-2 py-2 text-right tabular-nums text-muted-foreground">
                                {unitCost == null ? '—' : formatCurrency(unitCost * qty)}
                              </td>
                              <td className="px-2 py-2 text-right">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => setComponents((prev) => prev.filter((x) => x.key !== c.key))}
                                  aria-label={`Remove ${component?.sku ?? 'component'}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  !bomLoading && (
                    <p className="rounded-md border border-dashed border-border bg-secondary/40 px-3 py-2 text-xs text-muted-foreground">
                      No components on this form.
                    </p>
                  )
                )}

                {shortCount > 0 && (
                  <p className="text-xs text-fresh-salmon-dark">
                    {shortCount} component{shortCount === 1 ? '' : 's'} will go below zero at the selected
                    location. Double-check where they were pulled from, or submit anyway and reconcile with a
                    cycle count.
                  </p>
                )}
              </section>
            )}

            {locationRows.length === 0 && (
              <p className="rounded-md border border-dashed border-border bg-secondary/40 px-3 py-2 text-xs text-muted-foreground">
                No active locations yet.
              </p>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="prd-notes">Notes</Label>
              <Textarea
                id="prd-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Rework, substitutions, issues on the line…"
                rows={2}
                maxLength={2000}
              />
            </div>
          </div>

          <DialogFooter className="mt-6 sm:items-center">
            {item && goodUnits > 0 && (
              <span className="mr-auto text-xs text-muted-foreground">
                Est. build cost {formatCurrency(estComponentCost)}
                {estUnitCost != null && <> · {formatCurrency(estUnitCost)} / unit</>}
                {missingCost && ' · some components have no cost on file'}
              </span>
            )}
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || !item || goodUnits <= 0 || !toLocationId || locationRows.length === 0}
            >
              {submitting ? 'Submitting…' : 'Submit production'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
